import React, { useMemo } from "react";
import { formatDate } from "@/lib/dateFormat";

const STATUS_COLOR = {
  planned: "#94A3B8",
  in_progress: "#3B82F6",
  blocked: "#F54800",
  done: "#16A34A",
};

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

function fmtMonth(d) {
  return d.toLocaleDateString([], { month: "short", year: "2-digit" });
}

function daysBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// Treat each milestone as: starts where the previous one ended,
// ends on its own due_date. The very first milestone starts at the project start_date
// (or 14 days before the first due_date if no project start).
export default function GanttChart({ project, milestones, compact = false }) {
  const { rows, axis } = useMemo(() => {
    const sorted = [...milestones].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const dues = sorted.map((m) => parseDate(m.due_date)).filter(Boolean);
    const projStart = parseDate(project?.start_date);
    const projEnd = parseDate(project?.end_date);

    if (sorted.length === 0 || dues.length === 0) {
      return { rows: [], axis: { start: null, end: null, totalDays: 0 } };
    }

    const firstDue = dues[0];
    const lastDue = dues[dues.length - 1];
    const start = projStart && projStart < firstDue ? projStart : new Date(firstDue.getTime() - 14 * 24 * 60 * 60 * 1000);
    const end = projEnd && projEnd > lastDue ? projEnd : new Date(lastDue.getTime() + 3 * 24 * 60 * 60 * 1000);
    const totalDays = Math.max(1, daysBetween(start, end));

    let cursor = start;
    const out = sorted.map((m) => {
      const due = parseDate(m.due_date) || end;
      // Phase 21 — honour explicit milestone start_date when provided; otherwise chain from cursor.
      const explicitStart = parseDate(m.start_date);
      const startDate = explicitStart || cursor;
      const endDate = due > startDate ? due : new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
      const ms = { m, startDate, endDate };
      cursor = endDate;
      return ms;
    });

    // Build month tick marks
    const ticks = [];
    let t = new Date(start.getFullYear(), start.getMonth(), 1);
    while (t <= end) {
      const pct = Math.max(0, Math.min(100, (daysBetween(start, t) / totalDays) * 100));
      ticks.push({ label: fmtMonth(t), pct });
      t = new Date(t.getFullYear(), t.getMonth() + 1, 1);
    }

    const today = new Date();
    const todayPct = today >= start && today <= end ? (daysBetween(start, today) / totalDays) * 100 : null;

    return {
      rows: out.map(({ m, startDate, endDate }) => {
        const left = (daysBetween(start, startDate) / totalDays) * 100;
        const width = Math.max(1.5, (daysBetween(startDate, endDate) / totalDays) * 100);
        return { m, left, width };
      }),
      axis: { start, end, totalDays, ticks, todayPct },
    };
  }, [project, milestones]);

  if (rows.length === 0) {
    return compact ? null : (
      <div className="text-sm text-slate-500 px-5 py-6">No milestones to plot.</div>
    );
  }

  if (compact) {
    // Single-row strip — milestones stacked horizontally with the today marker
    return (
      <div className="relative h-2 bg-slate-100 rounded-sm overflow-hidden" data-testid="gantt-strip" title={`${rows.length} milestones`}>
        {rows.map(({ m, left, width }) => (
          <div
            key={m.id}
            className="absolute top-0 bottom-0"
            style={{ left: `${left}%`, width: `${width}%`, background: STATUS_COLOR[m.status] || STATUS_COLOR.planned }}
            title={`${m.title} — ${m.status}`}
          />
        ))}
        {axis.todayPct !== null && (
          <div className="absolute top-0 bottom-0 w-px bg-[#F54800]" style={{ left: `${axis.todayPct}%` }} />
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="gantt-chart">
      <div className="min-w-[640px] px-5 py-4 relative">
        {/* Axis */}
        <div className="relative h-6 border-b border-slate-200 mb-2">
          {axis.ticks.map((tk) => (
            <div key={tk.label} className="absolute top-0 h-full" style={{ left: `${tk.pct}%` }}>
              <div className="w-px h-full bg-slate-200" />
              <div className="absolute top-0 left-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{tk.label}</div>
            </div>
          ))}
          {axis.todayPct !== null && (
            <div className="absolute top-0 h-[300px]" style={{ left: `${axis.todayPct}%` }}>
              <div className="w-px h-full bg-[#F54800]" />
              <div className="absolute -top-3 -left-3 bg-[#F54800] text-white text-[9px] font-bold px-1 rounded-sm">TODAY</div>
            </div>
          )}
        </div>

        {/* Rows + dependency overlay container — relative so SVG can absolute-overlay */}
        <div className="relative">
          <div className="space-y-2" data-testid="gantt-rows">
            {rows.map(({ m, left, width }, i) => (
              <div key={m.id} className="grid grid-cols-[160px_1fr] gap-3 items-center" data-testid={`gantt-row-${m.id}`} data-row-index={i}>
                <div className="text-xs text-slate-700 truncate" title={m.title}>{m.title}</div>
                <div className="relative h-7 bg-slate-50 rounded-sm overflow-hidden">
                  <div
                    className="absolute top-1 bottom-1 rounded-sm flex items-center px-2 text-[10px] font-semibold text-white"
                    style={{ left: `${left}%`, width: `${width}%`, background: STATUS_COLOR[m.status] || STATUS_COLOR.planned, minWidth: 24 }}
                    title={`${m.title} — due ${formatDate(m.due_date)}`}
                  >
                    <span className="truncate">{formatDate(m.due_date, { fallback: "" })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dependency arrows — Phase 21
             Each row is 28px tall + 8px gap = 36px stride. The bar sits at top:4 / height:20 inside its row.
             We draw L-shaped paths from the right edge of the source bar down/up to the left edge of the dep bar. */}
          {(() => {
            const stride = 36;
            const trackLeftPct = 160 + 12; // 160px label + 12px gap
            // Convert percent within track to absolute "calc" string isn't trivial in SVG, so use a CSS overlay with a percentage-based viewBox.
            // We use a simple SVG with viewBox="0 0 100 H" where H = stride*rows.
            const H = rows.length * stride;
            const idx = Object.fromEntries(rows.map((r, i) => [r.m.id, i]));
            const lines = [];
            rows.forEach(({ m, left, width }, i) => {
              (m.dependencies || []).forEach((depId) => {
                const j = idx[depId];
                if (j === undefined || j === i) return;
                const src = rows[j];
                const x1 = src.left + src.width;     // right edge of source bar (in %)
                const y1 = j * stride + 14;          // vertical center of source row
                const x2 = left;                      // left edge of target bar
                const y2 = i * stride + 14;
                lines.push({ x1, y1, x2, y2, key: `${depId}->${m.id}` });
              });
            });
            if (lines.length === 0) return null;
            return (
              <svg
                className="absolute pointer-events-none"
                style={{ left: `${trackLeftPct}px`, top: 0, right: 0, height: H, width: `calc(100% - ${trackLeftPct}px)` }}
                viewBox={`0 0 100 ${H}`}
                preserveAspectRatio="none"
                data-testid="gantt-deps"
              >
                <defs>
                  <marker id="dep-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 Z" fill="#F54800" />
                  </marker>
                </defs>
                {lines.map((l) => {
                  // L-shaped path: right edge of source → horizontal jog → left edge of target.
                  const mid = Math.min(99, l.x2 - 1.5);
                  const path = `M ${l.x1} ${l.y1} L ${l.x1 + 0.7} ${l.y1} L ${l.x1 + 0.7} ${l.y2} L ${mid} ${l.y2}`;
                  return (
                    <path
                      key={l.key}
                      d={path}
                      fill="none"
                      stroke="#F54800"
                      strokeWidth="0.4"
                      vectorEffect="non-scaling-stroke"
                      markerEnd="url(#dep-arrow)"
                      opacity="0.7"
                    />
                  );
                })}
              </svg>
            );
          })()}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 flex-wrap text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {Object.entries(STATUS_COLOR).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: v }} /> {k.replace("_", " ")}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
